import React from "react";
import { useNavigate } from "@tanstack/react-router";
import {
  FileText,
  Loader2,
  Clock,
  XCircle,
  AlertTriangle,
  CheckCircle,
} from "lucide-react";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from "../../components/ui/card";
import { AIAgent } from "./types";
import { formatTimeAgo, formatDuration } from "./utils";

const TestRunRow: React.FC<{ testRun: any }> = ({ testRun }) => {
  const navigate = useNavigate();

  const getStatusIcon = (status: string) => {
    switch (status) {
      case "passed":
        return <CheckCircle className="h-4 w-4" />;
      case "failed":
        return <XCircle className="h-4 w-4" />;
      case "running":
        return <Loader2 className="h-4 w-4 animate-spin" />;
      case "pending":
        return <Clock className="h-4 w-4" />;
      default:
        return <AlertTriangle className="h-4 w-4" />;
    }
  };

  const handleClick = () => {
    navigate({
      to: "/test-runs/$testRunId",
      params: { testRunId: testRun.id },
    });
  };

  return (
    <div
      className="bg-gray-800 border border-gray-700 rounded-lg transition-all hover:bg-gray-750 mb-2 cursor-pointer"
      onClick={handleClick}
    >
      <div className="flex items-center justify-between p-4 flex-1 min-w-0">
        <div className="flex items-center space-x-4 flex-1 min-w-0">
          {/* Environment */}
          <span className="text-xs text-gray-400 truncate min-w-[80px]">
            {testRun.environment}
          </span>
          {/* Name and Trigger */}
          <div className="flex-1 min-w-0">
            <div className="flex items-center space-x-2">
              <h3 className="text-sm font-medium text-gray-100 truncate">
                {testRun.name}
              </h3>
              <div className="flex items-center space-x-1 text-xs text-gray-400">
                <span>Triggered by {testRun.triggeredBy}</span>
              </div>
            </div>
          </div>
          {/* Time and Duration */}
          <div className="flex items-center space-x-2 text-xs text-gray-400 min-w-[90px]">
            <span>{formatTimeAgo(testRun.startTime)}</span>
            <span>• {formatDuration(testRun.duration ?? 0)}</span>
          </div>
          {/* Stats */}
          <div className="flex items-center space-x-4 text-xs">
            <span className="text-gray-300">
              <span className="font-semibold">{testRun.totalTests}</span> Total
            </span>
            <span className="text-green-400">
              <span className="font-semibold">{testRun.passedTests}</span>{" "}
              Passed
            </span>
            <span className="text-red-400">
              <span className="font-semibold">{testRun.failedTests}</span>{" "}
              Failed
            </span>
            <span className="text-blue-400">
              <span className="font-semibold">{testRun.score}%</span> Score
            </span>
          </div>
        </div>
      </div>
    </div>
  );
};

export const RunsTab: React.FC<{ agent: AIAgent }> = ({ agent }) => {
  return (
    <div className="space-y-6">
      <Card className="bg-gray-800 border-gray-700">
        <CardHeader className="pb-2">
          <CardTitle className="text-gray-100 flex items-center space-x-2 text-lg">
            <FileText className="h-4 w-4" />
            <span>Test Runs</span>
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-2">
          {agent.testRuns.map((testRun) => (
            <TestRunRow key={testRun.id} testRun={testRun} />
          ))}
        </CardContent>
      </Card>
    </div>
  );
};
